<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import BaseBadge from '@/components/ui/BaseBadge.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useIssuesStore } from '@/stores/issues';
import type { IssueDetailDto } from '@/composables/useIssue';
import { SEVERITY_BADGE, SEVERITY_LABEL, STATUS_BADGE, STATUS_LABEL } from './issue-enums';

interface Props {
  issue: IssueDetailDto;
}
const props = defineProps<Props>();

const store = useIssuesStore();
const adjacent = computed(() => store.adjacentIds(props.issue.id));
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="issue-detail-header">
    <!-- Breadcrumb + prev/next -->
    <div class="flex items-center justify-between gap-2">
      <nav class="text-caption text-muted flex items-center gap-1" aria-label="Breadcrumb">
        <RouterLink to="/issues" class="hover:text-fg transition-colors">Issues</RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-fg truncate max-w-xs">{{ issue.title }}</span>
      </nav>

      <div class="flex items-center gap-1 flex-shrink-0">
        <BaseButton
          v-if="adjacent.prevId"
          variant="ghost"
          size="sm"
          :as="RouterLink"
          :to="`/issues/${adjacent.prevId}`"
          aria-label="Previous issue"
        >
          ← Prev
        </BaseButton>
        <BaseButton
          v-if="adjacent.nextId"
          variant="ghost"
          size="sm"
          :as="RouterLink"
          :to="`/issues/${adjacent.nextId}`"
          aria-label="Next issue"
        >
          Next →
        </BaseButton>
      </div>
    </div>

    <!-- Title + badges -->
    <div class="flex items-start gap-3">
      <div class="flex-1 min-w-0">
        <h1 class="text-page text-fg font-semibold leading-tight" data-testid="issue-title">
          {{ issue.title }}
        </h1>
        <p v-if="issue.location" class="text-caption text-muted mt-0.5">{{ issue.location }}</p>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <BaseBadge :variant="SEVERITY_BADGE[issue.severity] ?? 'info'">
          {{ SEVERITY_LABEL[issue.severity] ?? String(issue.severity) }}
        </BaseBadge>
        <BaseBadge :variant="STATUS_BADGE[issue.status] ?? 'info'" :no-dot="true">
          {{ STATUS_LABEL[issue.status] ?? String(issue.status) }}
        </BaseBadge>
      </div>
    </div>

    <!-- Reporter + time -->
    <p class="text-caption text-muted">
      Reported by
      <span class="text-fg">{{ issue.reporterDisplay }}</span>
      ·
      <time :datetime="issue.createdAt" class="font-mono tabular-nums">{{ issue.createdAt.split('T')[0] }}</time>
    </p>
  </div>
</template>
